import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Shield, TriangleAlert, Construction, X } from 'lucide-react';
import { SECTION_COLOR, CERVENA, nacti, posli, Nadpis, RadekPanelu, Tlacitko, tlumene, cara } from './ui';

/**
 * Dopravní info v navigaci rozvozce (2026-10-23) — Správa → Provozovna.
 *
 * Co navigace hlásí (limity, stopky, radary, hlášení řidičů, hlas), stav
 * dat z OpenStreetMap (obnova týdně, ručně tlačítkem) a poslední hlášení
 * řidičů s možností je zrušit.
 */
const TYPY = {
    hlidka: { nazev: 'Hlídka', Ikona: Shield, barva: '#2F6FEB' },
    nehoda: { nazev: 'Nehoda', Ikona: TriangleAlert, barva: CERVENA },
    prekazka: { nazev: 'Překážka', Ikona: Construction, barva: '#F59E0B' },
};

export default function DopravniInfoSprava({ isDark, muzeUpravovat = true }) {
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [obnovuji, setObnovuji] = useState(false);

    const nactiVse = () => nacti('/api/sprava/doprava').then(setData).catch((e) => setChyba(e.message));
    useEffect(() => { nactiVse(); }, []);

    if (!data) return chyba ? <p className="text-sm" style={{ color: CERVENA }}>{chyba}</p> : <Loader2 size={18} className="animate-spin" style={{ color: tlumene(isDark) }} />;

    const n = data.nastaveni;
    const uloz = async (zmena) => {
        setChyba(null);
        setData((d) => ({ ...d, nastaveni: { ...d.nastaveni, ...zmena } }));
        try {
            const r = await posli('/api/sprava/doprava', 'PUT', zmena);
            setData((d) => ({ ...d, nastaveni: r.nastaveni }));
        } catch (e) { setChyba(e.message); nactiVse(); }
    };
    const obnovit = async () => {
        setObnovuji(true);
        setChyba(null);
        try {
            const r = await posli('/api/sprava/doprava/obnovit', 'POST');
            setData((d) => ({ ...d, balicek: r.balicek }));
        } catch (e) { setChyba(e.message); }
        setObnovuji(false);
    };
    const zrusit = async (id) => {
        await posli(`/api/sprava/doprava/hlaseni/${id}`, 'DELETE').catch((e) => setChyba(e.message));
        nactiVse();
    };

    const b = data.balicek;
    const aktivni = data.hlaseni.filter((h) => h.plati);

    return (
        <div className="flex flex-col" style={{ gap: 20, maxWidth: 720 }}>
            <p className="text-[13px]" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                Co navigace v aplikaci rozvozců ukazuje a hlásí hlasem. Platí pro všechny telefony rozvozců najednou.
            </p>

            <div>
                <Nadpis isDark={isDark}>Upozornění</Nadpis>
                <Spinac isDark={isDark} disabled={!muzeUpravovat} zapnuto={n.limity} onZmena={(v) => uloz({ limity: v })}
                    popis="Rychlostní limity" detail="Značka limitu a rychlost na mapě, upozornění při překročení">
                    <Cislo isDark={isDark} disabled={!muzeUpravovat || !n.limity} hodnota={n.tolerance_kmh} min={0} max={20} jednotka="km/h nad limit"
                        onZmena={(v) => uloz({ tolerance_kmh: v })} />
                </Spinac>
                <Spinac isDark={isDark} disabled={!muzeUpravovat} zapnuto={n.stopky} onZmena={(v) => uloz({ stopky: v })}
                    popis="Stopky a přednost" detail="I najetí z vedlejší silnice na hlavní, kde značka v mapě chybí">
                    <Cislo isDark={isDark} disabled={!muzeUpravovat || !n.stopky} hodnota={n.predstih_m} min={40} max={300} krok={10} jednotka="m předem"
                        onZmena={(v) => uloz({ predstih_m: v })} />
                </Spinac>
                <Spinac isDark={isDark} disabled={!muzeUpravovat} zapnuto={n.radary} onZmena={(v) => uloz({ radary: v })}
                    popis="Radary a úsekové měření" detail="Jen ty, které leží na trase" />
                <Spinac isDark={isDark} disabled={!muzeUpravovat} zapnuto={n.hlaseni} onZmena={(v) => uloz({ hlaseni: v })}
                    popis="Hlášení od řidičů" detail="Hlídka, nehoda, překážka — vidí je ostatní řidiči">
                    <Cislo isDark={isDark} disabled={!muzeUpravovat || !n.hlaseni} hodnota={n.platnost_min} min={10} max={180} krok={5} jednotka="min platí"
                        onZmena={(v) => uloz({ platnost_min: v })} />
                </Spinac>
                <Spinac isDark={isDark} disabled={!muzeUpravovat} zapnuto={n.hlas} onZmena={(v) => uloz({ hlas: v })}
                    popis="Hlasová upozornění" detail="Krátké věty jako „Stopka za sto metrů“; vypnuté = jen na displeji" posledni />
            </div>

            <div>
                <Nadpis isDark={isDark} vpravo={muzeUpravovat && (
                    <Tlacitko isDark={isDark} Icon={obnovuji ? Loader2 : RefreshCw} disabled={obnovuji} onClick={obnovit}>
                        {obnovuji ? 'Stahuji…' : 'Obnovit teď'}
                    </Tlacitko>
                )}>Data z OpenStreetMap</Nadpis>
                {b ? (
                    <>
                        <RadekPanelu isDark={isDark} popis="Staženo" hodnota={new Date(b.vytvoreno).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })} />
                        <RadekPanelu isDark={isDark} popis="Úseků silnic" hodnota={`${b.cesty} (${b.odvozene} s limitem podle obce)`} />
                        <RadekPanelu isDark={isDark} popis="Stopky · přednosti" hodnota={`${b.stopky} · ${b.prednost}`} />
                        <RadekPanelu isDark={isDark} popis="Radary · úsekové měření" hodnota={`${b.radary} · ${b.useky}`} />
                        <Cislo isDark={isDark} disabled={!muzeUpravovat} hodnota={n.polomer_km} min={3} max={30} jednotka="km kolem bistra (projeví se při další obnově)"
                            onZmena={(v) => uloz({ polomer_km: v })} />
                    </>
                ) : (
                    <p className="text-[13px]" style={{ color: tlumene(isDark), margin: 0 }}>Data ještě nejsou stažená — obnovují se samy každé pondělí ráno, nebo hned tlačítkem.</p>
                )}
            </div>

            <div>
                <Nadpis isDark={isDark}>Hlášení od řidičů {aktivni.length > 0 ? `· ${aktivni.length} platí` : ''}</Nadpis>
                {data.hlaseni.length === 0 && <p className="text-[13px]" style={{ color: tlumene(isDark), margin: 0 }}>Zatím nikdo nic nenahlásil.</p>}
                {data.hlaseni.map((h) => {
                    const t = TYPY[h.typ];
                    return (
                        <div key={h.id} className="flex items-center gap-3" style={{ minHeight: 44, borderBottom: `1px solid ${cara(isDark)}`, opacity: h.plati ? 1 : 0.5 }}>
                            <t.Ikona size={18} color={t.barva} />
                            <span className="text-[13.5px] font-semibold" style={{ width: 80 }}>{t.nazev}</span>
                            <span className="text-[12.5px] flex-1 min-w-0" style={{ color: tlumene(isDark) }}>
                                {new Date(h.vytvoreno).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                {h.kdo ? ` · ${h.kdo}` : ''} · <a href={`https://mapy.com/?q=${h.lat},${h.lng}`} target="_blank" rel="noreferrer" style={{ color: SECTION_COLOR }}>mapa</a>
                                {h.plati ? '' : ' · prošlé'}
                            </span>
                            {h.plati && muzeUpravovat && (
                                <button type="button" onClick={() => zrusit(h.id)} title="Zrušit hlášení" style={{ background: 'none', border: 'none', color: tlumene(isDark), cursor: 'pointer', minWidth: 32, minHeight: 32 }}>
                                    <X size={15} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {chyba && <p className="text-[13px]" style={{ color: CERVENA, margin: 0 }}>{chyba}</p>}
        </div>
    );
}

function Spinac({ zapnuto, onZmena, popis, detail, children, isDark, disabled, posledni = false }) {
    return (
        <div style={{ padding: '10px 0', borderBottom: posledni ? 'none' : `1px solid ${cara(isDark)}` }}>
            <label className="flex items-center gap-3" style={{ cursor: disabled ? 'default' : 'pointer' }}>
                <span className="flex-1 min-w-0">
                    <span className="block text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>{popis}</span>
                    <span className="block text-[12.5px]" style={{ color: tlumene(isDark) }}>{detail}</span>
                </span>
                <input type="checkbox" role="switch" checked={!!zapnuto} disabled={disabled} onChange={(e) => onZmena(e.target.checked)}
                    style={{ width: 20, height: 20, accentColor: SECTION_COLOR, cursor: disabled ? 'default' : 'pointer' }} />
            </label>
            {children && <div style={{ marginTop: 6 }}>{children}</div>}
        </div>
    );
}

function Cislo({ hodnota, onZmena, min, max, krok = 1, jednotka, isDark, disabled }) {
    const [v, setV] = useState(hodnota);
    useEffect(() => setV(hodnota), [hodnota]);
    return (
        <label className="flex items-center gap-2 text-[12.5px]" style={{ color: tlumene(isDark), padding: '4px 0' }}>
            <input type="number" value={v} min={min} max={max} step={krok} disabled={disabled}
                onChange={(e) => setV(e.target.value)}
                onBlur={() => { const c = Math.max(min, Math.min(max, parseInt(v, 10) || min)); setV(c); if (c !== hodnota) onZmena(c); }}
                style={{ width: 68, height: 32, borderRadius: 8, padding: '0 8px', fontSize: 13.5, fontVariantNumeric: 'tabular-nums',
                    background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, color: 'var(--text-primary)' }} />
            {jednotka}
        </label>
    );
}
